import "../less/main.less";

import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router";

import { DigipostEntryPoint } from "../../types/entrypoint";
import { EntrypointLoadError } from "../../types/errors";
import App from "./App";
import appRoutes from "./appRoutes";
import { isProd } from "./env";
import ErrorPage from "./errorpage/ErrorPage";
import { ContextProviders } from "./infrastructure/context/ContextProviders";
import { getJSON } from "./infrastructure/digipost-fetch";
import { findUriIfExists } from "./infrastructure/link";
import { logError } from "./infrastructure/serverlog";
import { getTokenPathParam } from "./mobileapp/utils";

if (isProd) {
  Sentry.init({
    dsn: "https://eca0cbf822af5d7fa24fc86f679cf9ab@o516594.ingest.sentry.io/4505980764291072",
    allowUrls: [/www.digipost.no/],
    environment: isProd ? "production" : "development",
    release: __INNLOGGING_VERSION__,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["digipost-innlogging"],
        behaviour: "apply-tag-if-contains-third-party-frames",
      }),
    ],
    tracesSampleRate: 0,
    ignoreErrors: [
      "Failed to fetch",
      "NetworkError when attempting to fetch resource",
      "cancelled",
      "avbrutt",
      "Mistet nettverksforbindelsen.",
      "Internett ser ut til å være frakoblet.",
      "The network connection was lost.",
      "Load failed",
    ],
  });
}

window.addEventListener("error", (event) => {
  const eventErrorMessage = `${event.message} (In file ${event.filename}, line ${event.lineno} col ${event.colno}.)`;
  const error = event.error || Error(eventErrorMessage);

  logError(error, {
    origin: "window.addEventListener",
    eventErrorMessage,
  });
});

const entrypointLoader = async (): Promise<DigipostEntryPoint | null> => {
  return getJSON<DigipostEntryPoint>("/post/api").catch((error) => {
    //transformer til en EntrypointLoadError, slik at vi på feilsiden kan skille den fra andre
    const entrypointLoadError = new EntrypointLoadError(error);

    // Dersom entrytpoint-lasting feiler med "NOT_AUTHORIZED", og vi har en utloggingslenke tilgjengelig, logg brukeren ut umiddelbart.
    if (entrypointLoadError.apiError === "NOT_AUTHORIZED") {
      const registerPrivatLink = findUriIfExists(
        "register_person",
        entrypointLoadError.link ?? [],
      );

      if (registerPrivatLink !== undefined) {
        const oauthToken = getTokenPathParam(window.location.pathname);

        window.location.href =
          registerPrivatLink + (oauthToken ? `?token=${oauthToken}` : "");

        return Promise.resolve(null);
      }

      const logoutLink = findUriIfExists(
        "logout_idp",
        entrypointLoadError.link ?? [],
      );

      if (logoutLink !== undefined) {
        window.location.href = logoutLink;
        return Promise.resolve(null);
      }
    }

    return Promise.reject(entrypointLoadError);
  });
};

const router = createBrowserRouter([
  {
    path: "/innlogging/:language?",
    element: <App />,
    errorElement: <ErrorPage />,
    loader: entrypointLoader,
    children: appRoutes,
  },
]);
const el = document.querySelector("#root") as HTMLElement;

const root = createRoot(el);

root.render(
  <ContextProviders>
    <RouterProvider router={router} />
  </ContextProviders>,
);
